import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

interface PaymentLinkType {
  id: string;
  linkId: string;
  guestEmail: string;
  guestName?: string;
  locationName?: string;
  confirmationNumber?: string;
  arrivalDate?: string;
  customerId?: string;
  reservationId?: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  attempts?: number;
  tokenData?: string;
  metadata?: any;
  expiration?: string;
  ipAddress?: string;
  userAgent?: string;
  updatedAt?: string;
}

const AdminPage: React.FC = () => {
  // Form state
  const [guestName, setGuestName] = useState('John Smith');
  const [email, setEmail] = useState('test@example.com');
  const [locationName, setLocationName] = useState('Mint House Downtown Seattle');
  const [confirmationNumber, setConfirmationNumber] = useState('MH-2024-1215');
  const [customerId, setCustomerId] = useState('');
  const [arrivalDate, setArrivalDate] = useState(() => {
    const today = new Date();
    today.setDate(today.getDate() + 7); // Default to 1 week from now
    return today.toISOString().split('T')[0];
  });
  const [amount, setAmount] = useState(100);
  const [currency, setCurrency] = useState('USD');
  const [linkId, setLinkId] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Data state
  const [paymentLinks, setPaymentLinks] = useState<PaymentLinkType[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAllPaymentLinks();
  }, []);

  const fetchAllPaymentLinks = async () => {
    setLoading(true);
    try {
      const { data } = await client.models.PaymentLink.list();
      console.log('Raw data from DynamoDB:', data);
      
      // Log the first record's fields to check what we're getting
      if (data.length > 0) {
        const firstRecord = data[0];
        console.log('First record fields:', {
          guestName: firstRecord.guestName,
          locationName: firstRecord.locationName,
          confirmationNumber: firstRecord.confirmationNumber,
          arrivalDate: firstRecord.arrivalDate,
          // Log all fields to see what's available
          allFields: Object.keys(firstRecord)
        });
      }
      
      // Sort by createdAt with proper typing
      const sortedData = data.sort((a: any, b: any) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      
      console.log('Sorted data:', sortedData);
      setPaymentLinks(sortedData as PaymentLinkType[]);
    } catch (error) {
      console.error('Error fetching payment links:', error);
    }
    setLoading(false);
  };

  const createTestLink = async () => {
    setCreating(true);
    try {
      const testLinkId = `test-${Date.now()}`;
      const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
      
      const createData = {
        linkId: testLinkId,
        guestName: guestName,
        guestEmail: email,
        locationName: locationName,
        confirmationNumber: confirmationNumber,
        customerId: customerId || undefined,
        arrivalDate: arrivalDate,
        amount: amount,
        currency: currency,
        status: 'PENDING' as const,
        expiresAt: expiresAt,
        attempts: 0,
      };
      
      console.log('Creating payment link with full data:', createData);
      
      const result = await client.models.PaymentLink.create(createData);
      
      console.log('Create result:', result);
      
      if (result.errors && result.errors.length > 0) {
        console.error('❌ Creation failed:', result.errors);
        alert('Failed to create payment link: ' + JSON.stringify(result.errors));
        return;
      }

      setLinkId(testLinkId);
      await fetchAllPaymentLinks(); // Refresh the list
    } catch (error) {
      console.error('Error creating test link:', error);
      alert('Error creating payment link: ' + error);
    }
    setCreating(false);
  };

  const deletePaymentLink = async (id: string) => {
    // Use window.confirm instead of confirm to avoid ESLint error
    if (window.confirm('Are you sure you want to delete this payment link?')) {
      try {
        await client.models.PaymentLink.delete({ id });
        fetchAllPaymentLinks(); // Refresh the list
      } catch (error) {
        console.error('Error deleting payment link:', error);
      }
    }
  };

  return (
    <div style={{ padding: '40px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1>Payment Links Admin</h1>
      
      {/* Create New Link Section */}
      <div style={{ marginBottom: '40px', padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
        <h2>Create New Payment Link</h2>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div style={{ marginBottom: '20px' }}>
            <label>Guest Name:</label>
            <input 
              type="text" 
              value={guestName} 
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="e.g., John Smith"
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label>Email:</label>
            <input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label>Location:</label>
            <select 
              value={locationName} 
              onChange={(e) => setLocationName(e.target.value)}
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
            >
              <option value="Mint House Downtown Seattle">Mint House Downtown Seattle</option>
              <option value="Mint House Financial District">Mint House Financial District</option>
              <option value="Mint House Capitol Hill">Mint House Capitol Hill</option>
              <option value="Mint House South Lake Union">Mint House South Lake Union</option>
              <option value="Mint House Belltown">Mint House Belltown</option>
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label>Confirmation Number:</label>
            <input 
              type="text" 
              value={confirmationNumber} 
              onChange={(e) => setConfirmationNumber(e.target.value)}
              placeholder="e.g., MH-2024-1215"
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label>Customer ID (Mews):</label>
            <input 
              type="text" 
              value={customerId} 
              onChange={(e) => setCustomerId(e.target.value)}
              placeholder="e.g., 12345678-1234-5678-9abc-123456789012"
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label>Arrival Date:</label>
            <input 
              type="date" 
              value={arrivalDate} 
              onChange={(e) => setArrivalDate(e.target.value)}
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label>Amount:</label>
            <input 
              type="number" 
              value={amount} 
              onChange={(e) => setAmount(Number(e.target.value))}
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
            />
          </div>
          
          <div style={{ marginBottom: '20px' }}>
            <label>Currency:</label>
            <select 
              value={currency} 
              onChange={(e) => setCurrency(e.target.value)}
              style={{ width: '100%', padding: '8px', margin: '8px 0' }}
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="CAD">CAD</option>
            </select>
          </div>
        </div>
        
        <button 
          onClick={createTestLink} 
          disabled={creating}
          style={{ 
            padding: '12px 24px', 
            backgroundColor: '#667eea', 
            color: 'white', 
            border: 'none', 
            borderRadius: '8px',
            cursor: 'pointer'
          }}
        >
          {creating ? 'Creating...' : 'Create Payment Link'}
        </button>
        
        {linkId && (
          <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
            <h3>New Link Created!</h3>
            <p>Link ID: {linkId}</p>
            <a 
              href={`/pay/${linkId}`} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#667eea', textDecoration: 'none', fontWeight: 'bold' }}
            >
              Open Payment Link →
            </a>
          </div>
        )}
      </div>

      {/* Database Viewer Section */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>All Payment Links ({paymentLinks.length})</h2>
          <button 
            onClick={fetchAllPaymentLinks}
            disabled={loading}
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {loading ? (
          <p>Loading payment links...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Link ID</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Guest Name</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Location</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Confirmation</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Customer ID</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Arrival Date</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Transaction ID</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Created</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paymentLinks.map((link) => (
                  <tr key={link.id}>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <code style={{ fontSize: '12px' }}>{link.linkId}</code>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {link.guestName || (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>{link.guestEmail}</td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {link.locationName || (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {link.confirmationNumber || (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {link.customerId ? (
                        <code style={{ fontSize: '11px' }}>{link.customerId}</code>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {link.arrivalDate ? (
                        new Date(link.arrivalDate).toLocaleDateString()
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <span style={{ 
                        padding: '4px 8px', 
                        borderRadius: '4px', 
                        fontSize: '12px',
                        backgroundColor: link.status === 'COMPLETED' ? '#d4edda' : 
                                       link.status === 'PENDING' ? '#fff3cd' : 
                                       link.status === 'MEWS_PROCESSING' ? '#cce7ff' : '#f8d7da',
                        color: link.status === 'COMPLETED' ? '#155724' : 
                               link.status === 'PENDING' ? '#856404' : 
                               link.status === 'MEWS_PROCESSING' ? '#004085' : '#721c24'
                      }}>
                        {link.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {link.tokenData ? (
                        <code style={{ fontSize: '11px' }}>{link.tokenData}</code>
                      ) : (
                        <span style={{ color: '#999', fontStyle: 'italic' }}>-</span>
                      )}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      {new Date(link.createdAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}>
                      <a 
                        href={`/pay/${link.linkId}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ color: '#667eea', textDecoration: 'none', marginRight: '10px' }}
                      >
                        View
                      </a>
                      <button 
                        onClick={() => deletePaymentLink(link.id)}
                        style={{ 
                          padding: '4px 8px', 
                          backgroundColor: '#dc3545', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;